import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  Laptop, 
  Clock, 
  Save, 
  ExternalLink
} from 'lucide-react';
import './FormsUnified.css';

export default function ParentsPortalForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Parent Portal Access',
    title: 'Stay Connected & Informed',
    subtitle: "Access real-time updates on your child's progress, grades, schedules, and learning resources through our secure LMS portal",
    features: [
      { title: 'Real-Time Progress', description: "Monitor your child's academic performance with instant updates" },
      { title: 'Schedule & Events', description: 'Never miss important dates with integrated calendar' },
      { title: 'Secure Access', description: "Enterprise-grade security for your child's information" },
      { title: 'Mobile Friendly', description: 'Access anytime, anywhere from any device' }
    ],
    cta: {
      title: 'Access LMS Portal',
      description: 'Get instant access to grades, schedules, and learning resources',
      button_label: 'Launch Portal',
      button_url: 'https://classroom.google.com',
      note: 'Secure login • 24/7 Access'
    }
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    subtitle: false,
    features: false,
    cta: false
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=parents`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          s.section_type === 'parents-portal' || 
          s.section_type === 'parent-portal-access' || 
          s.section_type === 'portal'
        );
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            s.section_type === 'parents-portal' || 
            s.section_type === 'parent-portal-access' || 
            s.section_type === 'portal'
          );
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const loadedData = {
          kicker: en.kicker || 'Parent Portal Access',
          title: en.title || 'Stay Connected & Informed',
          subtitle: en.subtitle || en.description || formData.subtitle,
          features: Array.isArray(en.features) && en.features.length > 0 ? en.features : formData.features,
          cta: {
            ...formData.cta,
            ...(en.cta || {})
          }
        };

        setFormData(loadedData);
        setOriginalData(loadedData);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, subtitle: false, features: false, cta: false };

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'subtitle', 'features', 'cta'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  }
                }
              });

              setPendingFields(pFields);
            }
          }
        } catch (pe) {
          console.warn('[ParentsPortalForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[ParentsPortalForm] Load error:', err);
      toast.error('Gagal memuat konten Parent Portal');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCtaChange = (subField, value) => {
    setFormData(prev => ({
      ...prev,
      cta: { ...prev.cta, [subField]: value }
    }));
  };

  const handleFeatureChange = (index, subField, value) => {
    setFormData(prev => {
      const next = [...prev.features];
      next[index] = { ...next[index], [subField]: value };
      return { ...prev, features: next };
    });
  };

  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" berhasil diajukan untuk approval Kepala Sekolah`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Pengajuan gagal');
      }
    } catch (err) {
      console.error('[ParentsPortalForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Parent Portal content...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Laptop size={22} />
        </div>
        <div>
          <h2 className="unified-title">Parent Portal Access (LMS)</h2>
          <p className="unified-subtitle">
            Kelola informasi portal belajar orang tua, tautan direct link "Launch Portal", serta poin keunggulan akses portal sekolah.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Parent Portal Access"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Stay Connected & Informed"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Subtitle */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={pendingFields.subtitle}
            placeholder="Access real-time updates on your child's progress, grades, schedules..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', formData.subtitle, 'Section Subtitle')}
            disabled={pendingFields.subtitle || submitting.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : (pendingFields.subtitle ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Portal CTA Action Box (Launch Portal Link) */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Portal CTA Action Box & Direct Link</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Pengaturan kotak aksi portal di sebelah kanan serta URL tujuan yang dibuka saat orang tua mengklik tombol "Launch Portal".
            </p>
          </div>
          {pendingFields.cta && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px', marginTop: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>CTA Card Title</label>
              <input
                type="text"
                className="unified-input"
                value={formData.cta.title || ''}
                disabled={pendingFields.cta}
                onChange={(e) => handleCtaChange('title', e.target.value)}
                placeholder="e.g. Access LMS Portal"
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>CTA Card Note / Keamanan</label>
              <input
                type="text"
                className="unified-input"
                value={formData.cta.note || ''}
                disabled={pendingFields.cta}
                onChange={(e) => handleCtaChange('note', e.target.value)}
                placeholder="e.g. Secure login • 24/7 Access"
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>CTA Description</label>
            <input
              type="text"
              className="unified-input"
              value={formData.cta.description || ''}
              disabled={pendingFields.cta}
              onChange={(e) => handleCtaChange('description', e.target.value)}
              placeholder="e.g. Get instant access to grades, schedules, and learning resources"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>Tombol Label (Button Text)</label>
              <input
                type="text"
                className="unified-input"
                value={formData.cta.button_label || ''}
                disabled={pendingFields.cta}
                onChange={(e) => handleCtaChange('button_label', e.target.value)}
                placeholder="e.g. Launch Portal"
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                Tautan Tujuan Portal (Launch Portal Link URL) *
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.cta.button_url || ''}
                  disabled={pendingFields.cta}
                  onChange={(e) => handleCtaChange('button_url', e.target.value)}
                  placeholder="e.g. https://classroom.google.com"
                  style={{ flex: 1 }}
                />
                {formData.cta.button_url && (
                  <a
                    href={formData.cta.button_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="unified-secondary-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', textDecoration: 'none', padding: '0 14px' }}
                    title="Uji coba buka link portal di tab baru"
                  >
                    Buka Link <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('cta', formData.cta, 'Portal CTA Action Box')}
            disabled={pendingFields.cta || submitting.cta}
          >
            <Save size={14} /> {submitting.cta ? 'Saving...' : (pendingFields.cta ? 'Pending' : 'Save Portal CTA')}
          </button>
        </div>
      </div>

      {/* Portal Features Grid (4 items) */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Portal Features (4 Keunggulan)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Daftar keunggulan portal yang ditampilkan pada kolom kiri seksi portal.
            </p>
          </div>
          {pendingFields.features && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '16px', marginTop: '14px' }}>
          {formData.features.map((feat, fIdx) => (
            <div key={fIdx} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>
                Fitur #{fIdx + 1}
              </label>
              <input
                type="text"
                className="unified-input"
                value={feat.title}
                disabled={pendingFields.features}
                onChange={(e) => handleFeatureChange(fIdx, 'title', e.target.value)}
                style={{ marginBottom: '8px', fontWeight: 600 }}
              />
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block' }}>
                Deskripsi
              </label>
              <textarea
                className="unified-textarea"
                rows={2}
                value={feat.description}
                disabled={pendingFields.features}
                onChange={(e) => handleFeatureChange(fIdx, 'description', e.target.value)}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('features', formData.features, 'Portal Features')}
            disabled={pendingFields.features || submitting.features}
          >
            <Save size={14} /> {submitting.features ? 'Saving...' : (pendingFields.features ? 'Pending' : 'Save Features')}
          </button>
        </div>
      </div>

    </div>
  );
}
